import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';

interface Station {
  id: string; name: string; brand?: string; source: string;
  lat: number; lng: number; distanceMiles: number;
  prices?: Record<string, number>;
}

export default function MapPage() {
  const { idToken } = useAuth();
  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [me, setMe] = useState<any>(null);
  const [zip, setZip] = useState<string>('');
  const [miles, setMiles] = useState<number>(5);
  const [data, setData] = useState<{ center: any; stations: Station[] } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      // Init on first visit (stamps cognito attrs from signup zip if needed).
      await api('/api/me/init', { method: 'POST', token: idToken!, body: '{}' }).catch(() => {});
      const m = await api('/api/me', { token: idToken! });
      setMe(m);
      setZip(m.zip);
      // Pre-load 5-mile radius
      await loadStations(m.zip, 5);
    })();
  }, []);

  async function loadStations(z: string, mi: number) {
    setLoading(true);
    try {
      const res = await api(`/api/stations?zip=${z}&miles=${mi}`, { token: idToken! });
      setData(res);
      renderMap(res);
    } finally { setLoading(false); }
  }

  function renderMap(res: { center: any; stations: Station[]; radiusMiles: number }) {
    if (!mapEl.current) return;
    if (!mapRef.current) {
      mapRef.current = new maplibregl.Map({
        container: mapEl.current,
        style: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
        center: [res.center.lng, res.center.lat],
        zoom: zoomForMiles(res.radiusMiles ?? miles),
      });
    } else {
      mapRef.current.flyTo({ center: [res.center.lng, res.center.lat], zoom: zoomForMiles(res.radiusMiles ?? miles) });
      mapRef.current.getCanvas().querySelectorAll('.gg-marker').forEach(n => n.remove());
    }
    // Clear old markers
    (mapRef.current as any)._ggMarkers?.forEach((m: maplibregl.Marker) => m.remove());
    const markers: maplibregl.Marker[] = [];
    for (const s of res.stations) {
      const el = document.createElement('div');
      el.className = 'gg-marker';
      el.style.cssText = 'width:18px;height:18px;border-radius:9px;background:#22d3ee;border:2px solid #0b1220;box-shadow:0 0 4px rgba(34,211,238,.7);';
      const m = new maplibregl.Marker(el)
        .setLngLat([s.lng, s.lat])
        .setPopup(new maplibregl.Popup({ offset: 12 }).setHTML(
          `<strong>${escapeHtml(s.name)}</strong><br/>${s.brand ?? ''}<br/>${s.distanceMiles} mi<br/>${s.prices?.regular ? `$${s.prices.regular.toFixed(4)}/gal` : '<em>no price</em>'}`,
        ))
        .addTo(mapRef.current!);
      markers.push(m);
    }
    (mapRef.current as any)._ggMarkers = markers;
  }

  function zoomForMiles(mi: number) {
    // Rough mapping.
    if (mi <= 5) return 12.2;
    if (mi <= 10) return 11.2;
    if (mi <= 25) return 10.0;
    return 8.8;
  }

  const extraZips: string[] = me?.extraZips ?? [];

  return (
    <div className="container">
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="row" style={{ gap: 14, flexWrap: 'wrap' }}>
          <div>
            <label>Zip</label>
            <select value={zip} onChange={e => { setZip(e.target.value); loadStations(e.target.value, miles); }}>
              {[me?.zip, ...extraZips].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).map(z => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>
          <div>
            <label>Radius (mi) — max {me?.radius ?? 50}</label>
            <input type="range" min={1} max={me?.radius ?? 50} value={miles}
              onChange={e => setMiles(parseInt(e.target.value, 10))}
              onMouseUp={() => loadStations(zip, miles)} onTouchEnd={() => loadStations(zip, miles)} />
            <span className="muted small" style={{ marginLeft: 8 }}>{miles} mi</span>
          </div>
          <div className="spacer" />
          {loading && <span className="muted small">Loading…</span>}
          <span className="muted small">{data?.stations.length ?? 0} stations</span>
        </div>
      </div>

      <div className="grid two">
        <div className="map" ref={mapEl} />
        <div className="card" style={{ maxHeight: 540, overflow: 'auto' }}>
          <h3>Stations</h3>
          <table>
            <thead><tr><th>Station</th><th>Brand</th><th>Dist</th><th>Regular</th></tr></thead>
            <tbody>
              {data?.stations.map(s => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td className="muted small">{s.brand ?? '—'}</td>
                  <td>{s.distanceMiles}</td>
                  <td>{s.prices?.regular ? `$${s.prices.regular.toFixed(4)}` : <span className="muted small">—</span>}</td>
                </tr>
              ))}
              {data && data.stations.length === 0 && <tr><td colSpan={4} className="muted">No stations in range.</td></tr>}
            </tbody>
          </table>
          {extraZips.length === 0 && (
            <p className="notice" style={{ marginTop: 14 }}>
              Want more zip codes in the dropdown? An admin can add them to your profile.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function escapeHtml(s: string) { return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!)); }
